import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * Sparkline — a compact chart for KPI tiles.
 *
 * variant "line"   → smooth line with visible min/max Y ticks and first/last
 *                    X ticks so the tile is self-explanatory at a glance.
 * variant "bars"   → discrete monthly totals, axis-less (used in denser blocks).
 *
 * `yFormat` — formatter for Y-axis ticks (e.g. money, ×, count). Defaults to
 * a compact locale string.
 * `xShort` — formatter for X-axis ticks (defaults to "YY-MM" → "Jun '26").
 */
const _defaultY = (v) => {
  if (typeof v !== "number") return v;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return v.toString();
};
const _defaultX = (p) => {
  if (typeof p !== "string" || p.length < 7) return p;
  const [y, m] = p.split("-");
  const short = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m, 10) - 1];
  return `${short} '${y.slice(2)}`;
};

export default function Sparkline({
  series = [], stroke = "#1D57A5", ytdStroke = "#006747",
  height = 44, variant = "line", delta = null, invertDelta = false,
  yFormat = _defaultY, xFormat = _defaultX,
}) {
  const data = (series || []).filter((r) => r.value !== null && r.value !== undefined);
  const hasData = data.length >= 2;
  const values = data.map((r) => r.value);
  const yMin = Math.min(...values);
  const yMax = Math.max(...values);

  return (
    <div className="w-full">
      <div style={{ height }} className="w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            {variant === "bars" ? (
              <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <YAxis hide domain={[0, "dataMax"]} />
                <Tooltip
                  cursor={{ fill: "#FFD100", fillOpacity: 0.15 }}
                  contentStyle={{ background: "#141B4D", border: "none",
                                  color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
                  itemStyle={{ color: "#F2E9DB" }}
                  labelStyle={{ color: "#FFD100", fontWeight: 700 }}
                  formatter={(v) => (typeof v === "number") ? v.toLocaleString() : v}
                  labelFormatter={(l, payload) => {
                    const p = payload?.[0]?.payload;
                    return p?.period + (p?.is_partial ? " (partial)" : "");
                  }}
                />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {data.map((r, i) => (
                    <Cell key={i}
                      fill={r.in_ytd ? ytdStroke : stroke}
                      fillOpacity={r.is_partial ? 0.4 : 1} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={data} margin={{ top: 6, right: 6, bottom: 2, left: 2 }}>
                <XAxis dataKey="period"
                  tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.6 }}
                  tickFormatter={xFormat}
                  axisLine={false} tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={30} />
                <YAxis
                  tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.6 }}
                  tickFormatter={yFormat}
                  axisLine={false} tickLine={false}
                  width={32}
                  ticks={[yMin, yMax]}
                  domain={[yMin, yMax]} />
                <Tooltip
                  cursor={{ stroke: "#FFD100", strokeWidth: 1, strokeOpacity: 0.5 }}
                  contentStyle={{ background: "#141B4D", border: "none",
                                  color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
                  itemStyle={{ color: "#F2E9DB" }}
                  labelStyle={{ color: "#FFD100", fontWeight: 700 }}
                  formatter={(v) => (typeof v === "number") ? v.toLocaleString() : v}
                  labelFormatter={(l, payload) => {
                    const p = payload?.[0]?.payload;
                    return p?.period || l;
                  }}
                />
                <Line type="monotone" dataKey="value" stroke={stroke}
                      strokeWidth={1.75} dot={false} isAnimationActive={false}
                      activeDot={{ r: 3, fill: stroke, stroke: "#FFD100", strokeWidth: 2 }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center text-[10px] opacity-40 italic">
            — no history yet —
          </div>
        )}
      </div>
      {delta && (delta.mom != null || delta.yoy != null) ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {delta.mom != null && <DeltaBadge label="MoM" pct={delta.mom} invert={invertDelta} />}
          {delta.yoy != null && <DeltaBadge label="YoY" pct={delta.yoy} invert={invertDelta} />}
        </div>
      ) : delta && delta.pct != null ? (
        <DeltaBadge label="vs prior 7d" pct={delta.pct} invert={invertDelta} />
      ) : null}
    </div>
  );
}

function DeltaBadge({ label, pct, invert }) {
  const isUp = pct > 0.5;
  const isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen" : isBad ? "text-red-700" : "text-midnight opacity-60";
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  return (
    <div className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${color}`}>
      <span className="opacity-60 tracking-widest">{label}</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct}%
    </div>
  );
}
